/**
 * Responsibility: Implement object check behavior for the engine layer.
 * Scope: Owns engine-layer concerns only; cross-layer orchestration is excluded.
 */
import type { SupervisorCheckFn, SupervisorIncidentSeed } from "@anyquest/shared-core"
import { findDuplicateNames } from './common'

export const objectCheck: SupervisorCheckFn = (state) => {
  const incidents: SupervisorIncidentSeed[] = []

  const roomItems = (state.currentRoom?.items ?? []).map(v => String(v ?? '').trim())
  const invalidRoomItems = roomItems.filter(v => !v).length
  if (invalidRoomItems > 0) {
    incidents.push({
      code: 'object_room_items_invalid',
      domain: 'object',
      severity: 'low',
      confidence: 0.99,
      message: `${invalidRoomItems} room item entries are empty.`,
      remediationPlan: 'Trim room items and remove empty entries.',
      context: { invalidRoomItems },
    })
  }

  const duplicateRoomItems = findDuplicateNames(roomItems)
  if (duplicateRoomItems.length > 0) {
    incidents.push({
      code: 'object_room_items_duplicate',
      domain: 'object',
      severity: 'low',
      confidence: 0.95,
      message: `Room has duplicate item names (${duplicateRoomItems.length}).`,
      remediationPlan: 'Deduplicate room item names case-insensitively.',
      context: { duplicates: duplicateRoomItems.slice(0, 12) },
    })
  }

  let invalidRoomNpcs = 0
  let duplicateRoomNpcNames = 0
  const roomNpcNames = new Set<string>()
  for (const npc of state.currentRoom?.npcs ?? []) {
    const name = String(npc?.name ?? '').trim()
    const idn = String(npc?.identity ?? '').trim()
    if (!name || !idn) invalidRoomNpcs += 1
    const key = name.toLowerCase()
    if (name) {
      if (roomNpcNames.has(key)) duplicateRoomNpcNames += 1
      roomNpcNames.add(key)
    }
  }

  if (invalidRoomNpcs > 0) {
    incidents.push({
      code: 'object_room_npcs_invalid',
      domain: 'object',
      severity: 'medium',
      confidence: 0.97,
      message: `${invalidRoomNpcs} room NPC entries are missing required fields.`,
      remediationPlan: 'Prune invalid room NPC entries.',
      context: { invalidRoomNpcs },
    })
  }

  if (duplicateRoomNpcNames > 0) {
    incidents.push({
      code: 'object_room_npcs_duplicate_names',
      domain: 'object',
      severity: 'low',
      confidence: 0.95,
      message: `${duplicateRoomNpcNames} duplicate room NPC names detected.`,
      remediationPlan: 'Deduplicate room NPCs by name.',
      context: { duplicateRoomNpcNames },
    })
  }

  let invalidWorldNpcs = 0
  const worldNpcIds = new Set<string>()
  let duplicateWorldNpcIds = 0
  for (const npc of state.world?.npcs ?? []) {
    const id = String(npc?.id ?? '').trim()
    const name = String(npc?.name ?? '').trim()
    const loc = String(npc?.location ?? '').trim()
    if (!id || !name || !loc) invalidWorldNpcs += 1
    if (id) {
      if (worldNpcIds.has(id)) duplicateWorldNpcIds += 1
      worldNpcIds.add(id)
    }
  }

  if (invalidWorldNpcs > 0) {
    incidents.push({
      code: 'object_world_npcs_invalid',
      domain: 'object',
      severity: 'medium',
      confidence: 0.98,
      message: `${invalidWorldNpcs} world NPC entries are missing required fields.`,
      remediationPlan: 'Normalize missing world NPC fields and drop irrecoverable entries.',
      context: { invalidWorldNpcs },
    })
  }

  if (duplicateWorldNpcIds > 0) {
    incidents.push({
      code: 'object_world_npcs_duplicate_ids',
      domain: 'object',
      severity: 'medium',
      confidence: 0.99,
      message: `${duplicateWorldNpcIds} duplicate world NPC IDs detected.`,
      remediationPlan: 'Regenerate duplicate world NPC IDs.',
      context: { duplicateWorldNpcIds },
    })
  }

  return { passed: incidents.length === 0, incidents }
}
